import { Button, makeStyles, } from '@material-ui/core';
import { Close } from "@material-ui/icons";
import { Skeleton } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IBusinessUnit, getAll } from '../../../../../services/business.service';
import { addUserToGroup } from '../../../../../services/groups.service';
import { IFunctionality } from '../../components/Boxes/FuncionalityAcessCard';
import BusinessUnitCard from '../../components/Cards/BusinessUnitCard';
import { ActionButtonsContainer, BoxesContainer, CloseButton, Container, Header, HeaderTitle, SelectorContainer, } from './styles';

export interface IGroup {
    id: string; 
    name: string; 
    committeePackageName: string; 
    committeePackageId: string; 
    accessModel: {
      allResources: boolean;
      onlyRelatedToDirectory: boolean;
      onlyRelatedToOwner: boolean;
    };
    participants: { id: string; name: string; }[];
    selectedFunctionalities: string[];
};
  
interface IAddUserToGroupGroupModal {
    setModalStatus: (status: boolean) => void;
    handleAddToNewGroup: (id: string) => Promise<void>;
    userId: string;
    setToastMessage: React.Dispatch<React.SetStateAction<{
        open: boolean;
        content: string;
        type: string;
        autoHideDuration: number;
    }>>;
};

export interface IFormattedFunctionality extends IFunctionality {
    hidden: boolean;
}

const useStyles = makeStyles((theme) => ({
    title: {
        ...theme.typography.h4,
        fontSize: '20px'
    },
    button: {
        ...theme.typography.subtitle1,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        textTransform: "capitalize",
    },
    buttonOutlined: {
        border: "1px solid",
        fontSize: "1rem",
        fontWeight: "bold",
        color: "#666666",
        textTransform: "capitalize",
    },
}));
  
const AddUserToGroupGroupModal: React.FC<IAddUserToGroupGroupModal> = ({
  setModalStatus,
  handleAddToNewGroup,
  userId,
  setToastMessage
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const effectHasAlreadyRunned = useRef(false);

    const [loading, setLoading] = useState<boolean>(true);
    const [selectedGroup, setSelectedGroup] = useState<string | undefined>();
    const [businessUnits, setBusinessUnits] = useState<IBusinessUnit[]>([]);

    const fetchBusiness = useCallback(async () => {
        effectHasAlreadyRunned.current = true;

        setLoading(true);
    
        const { businessUnits } = await getAll();
    
        setBusinessUnits(businessUnits);

        setLoading(false);
    }, []);
    
    useEffect(() => {        
        if (effectHasAlreadyRunned.current) {
            return;
        };
        
        fetchBusiness();
    }, [effectHasAlreadyRunned]);

    const handleSave = useCallback(async() => {
        if(!selectedGroup) return;

        try {
            await addUserToGroup({
                userId,
                groupId: selectedGroup as string
            });

            handleAddToNewGroup(userId);
            setModalStatus(false);
        } catch (error) {
            setToastMessage({
                open: true,
                content: t(`softwareManagement.modules.users.errorAddingUserToGroup`),
                type: 'error',
                autoHideDuration: 6000
            });
        }
    }, [selectedGroup]);
    
    return (
        <Container>
            <Header>
                <HeaderTitle className={classes.title}>
                    {t(`softwareManagement.modules.users.addGroupToUser`)}
                </HeaderTitle>

                <CloseButton onClick={() => setModalStatus(false)}>
                    <Close />
                </CloseButton>
            </Header>

            <SelectorContainer>
                {loading ? (
                    <>
                        <Skeleton variant={"rectangular"} style={{ borderRadius: '8px', width: '100%', height: '300px' }}/>
                        <Skeleton variant={"rectangular"} style={{ borderRadius: '8px', width: '100%', height: '300px' }}/>
                    </>
                ) : (
                    <>
                        <HeaderTitle>
                            {t(`softwareManagement.modules.groups.selectedDesiredGroup`)}
                        </HeaderTitle>

                        <BoxesContainer>
                            {loading && (
                                <>
                                    <Skeleton variant={"rectangular"} style={{ borderRadius: '8px', width: '100%', height: '300px' }}/>
                                    <Skeleton variant={"rectangular"} style={{ borderRadius: '8px', width: '100%', height: '300px' }}/>
                                    <Skeleton variant={"rectangular"} style={{ borderRadius: '8px', width: '100%', height: '300px' }}/>
                                    <Skeleton variant={"rectangular"} style={{ borderRadius: '8px', width: '100%', height: '300px' }}/>
                                </>
                            )}
                            {businessUnits.map((businessUnit) => (
                                <BusinessUnitCard 
                                    key={businessUnit.id} 
                                    businessUnit={businessUnit}  
                                    groupSelectable
                                    selectedGroup={selectedGroup}
                                    setSelectedGroup={setSelectedGroup}
                                />
                            ))}
                        </BoxesContainer>
                    </>
                )}
            </SelectorContainer>

            <ActionButtonsContainer>
                <Button
                    variant="outlined"
                    className={classes.buttonOutlined}
                    onClick={() => setModalStatus(false)}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    className={classes.button}
                    onClick={handleSave}
                    disabled={!selectedGroup}
                >
                    Salvar
                </Button>
            </ActionButtonsContainer>
        </Container>
    )
}

export default AddUserToGroupGroupModal;